import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { internalAuditApi } from "./client";

export const internalAuditKeys = {
  all: ["internal-audit"] as const,
  plans: (params?: { limit?: number; offset?: number }) =>
    [...internalAuditKeys.all, "plans", params?.limit, params?.offset] as const,
  planDetail: (planId: string) =>
    [...internalAuditKeys.all, "plan", planId] as const,
  records: (params?: { limit?: number; offset?: number }) =>
    [...internalAuditKeys.all, "records", params?.limit, params?.offset] as const,
  recordDetail: (id: string) =>
    [...internalAuditKeys.all, "detail", id] as const,
};

export function useInternalAuditPlansQuery(options: {
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: internalAuditKeys.plans({
      limit: options.limit,
      offset: options.offset,
    }),
    queryFn: () =>
      internalAuditApi.listPlans({
        limit: options.limit,
        offset: options.offset,
      }),
    staleTime: 1000 * 60,
  });
}

export function useInternalAuditRecordsQuery(options: {
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: internalAuditKeys.records({
      limit: options.limit,
      offset: options.offset,
    }),
    queryFn: () =>
      internalAuditApi.listRecords({
        limit: options.limit,
        offset: options.offset,
      }),
    staleTime: 1000 * 60,
  });
}

export function useInternalAuditRecordDetailQuery(
  recordId: string,
  options: { enabled: boolean },
) {
  return useQuery({
    queryKey: internalAuditKeys.recordDetail(recordId),
    queryFn: () => internalAuditApi.getRecord(recordId),
    enabled: options.enabled && Boolean(recordId),
    staleTime: 1000 * 60,
  });
}

export function useAuditPlanDetailQuery(
  planId: string,
  options: { enabled: boolean },
) {
  return useQuery({
    queryKey: internalAuditKeys.planDetail(planId),
    queryFn: () => internalAuditApi.getPlan(planId),
    enabled: options.enabled && Boolean(planId),
    staleTime: 1000 * 60,
  });
}

export function useCreateAuditRecordMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: internalAuditApi.createRecord,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: internalAuditKeys.all });
    },
  });
}
